import ExcelJS from 'exceljs';
import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import type { DemDeliveryTrip, DemDispatchSettings, DemDriver, DemTripOrderStop } from '../types/dem';

const TRIP_SELECT =
  'trip_id,trip_date,driver_name,driver_assistant_name,status,current_lat,current_lng';
const STOP_SELECT =
  'id,trip_id,order_no,customer_code,customer_name,address,postal,latitude,longitude,stop_sequence';

export function tripShortLabel(tripId: string): string {
  const parts = tripId.split('-');
  return parts.length >= 3 ? parts.slice(2).join('-') : tripId;
}

export function singaporeYmd(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Singapore',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const utc = Date.UTC(y, m - 1, d + days, 12);
  return singaporeYmd(new Date(utc));
}

export async function fetchDeliveryTrips(tripDate: string): Promise<DemDeliveryTrip[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('delivery_trips')
    .select(TRIP_SELECT)
    .eq('trip_date', tripDate)
    .order('trip_id');

  if (error) throw new Error(formatSupabaseError(error));

  return (data ?? []).map((row) => {
    const r = row as Record<string, unknown>;
    return {
      trip_id: String(r.trip_id ?? ''),
      trip_date: String(r.trip_date ?? ''),
      driver_name: String(r.driver_name ?? 'Unassigned'),
      driver_assistant_name: String(r.driver_assistant_name ?? ''),
      status: String(r.status ?? 'Planning'),
      current_lat: r.current_lat == null ? null : Number(r.current_lat),
      current_lng: r.current_lng == null ? null : Number(r.current_lng),
    };
  });
}

export async function fetchTripStops(tripId: string): Promise<DemTripOrderStop[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('trip_order_mapping')
    .select(STOP_SELECT)
    .eq('trip_id', tripId)
    .order('stop_sequence');

  if (error) throw new Error(formatSupabaseError(error));

  return (data ?? []).map((row) => {
    const r = row as Record<string, unknown>;
    return {
      id: String(r.id ?? ''),
      trip_id: String(r.trip_id ?? ''),
      order_no: String(r.order_no ?? ''),
      customer_code: String(r.customer_code ?? ''),
      customer_name: String(r.customer_name ?? ''),
      address: String(r.address ?? ''),
      postal: String(r.postal ?? ''),
      latitude: r.latitude == null ? null : Number(r.latitude),
      longitude: r.longitude == null ? null : Number(r.longitude),
      stop_sequence: Number(r.stop_sequence ?? 0),
    };
  });
}

export async function fetchDrivers(): Promise<DemDriver[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db.from('drivers').select('id, name, phone, role').order('role').order('name');

  if (error) throw new Error(formatSupabaseError(error));

  return (data ?? []).map((row) => {
    const r = row as Record<string, unknown>;
    return {
      id: Number(r.id ?? 0),
      name: String(r.name ?? ''),
      phone: String(r.phone ?? ''),
      role: String(r.role ?? 'Driver'),
    };
  });
}

export async function addDriver(payload: { name: string; phone: string; role: string }): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db.from('drivers').insert(payload);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function deleteDriver(id: number): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db.from('drivers').delete().eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function fetchDispatchSettings(): Promise<DemDispatchSettings> {
  const db = getDbSupabase('dem');
  const { data, error } = await db.from('dispatch_settings').select('key, value');

  if (error) throw new Error(formatSupabaseError(error));

  const map = new Map<string, string>();
  for (const row of data ?? []) {
    const r = row as Record<string, unknown>;
    map.set(String(r.key ?? ''), String(r.value ?? ''));
  }

  const optimizeByTrip: Record<string, boolean> = {};
  for (const [key, value] of map.entries()) {
    if (key.startsWith('opt_')) {
      optimizeByTrip[key.slice(4)] = value === 'true';
    }
  }

  return {
    hubPostal: map.get('start_postal_code') ?? '533756',
    defaultDepart: map.get('default_depart_time') ?? '08:30',
    defaultLunch: map.get('default_lunch_time') ?? '12:00',
    optimizeByTrip,
  };
}

export async function saveDispatchSettings(settings: {
  hubPostal: string;
  defaultDepart: string;
  defaultLunch: string;
}): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db.from('dispatch_settings').upsert([
    { key: 'start_postal_code', value: settings.hubPostal },
    { key: 'default_depart_time', value: settings.defaultDepart },
    { key: 'default_lunch_time', value: settings.defaultLunch },
  ]);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function saveTripOptimizeSetting(tripId: string, enabled: boolean): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db.from('dispatch_settings').upsert({
    key: `opt_${tripId}`,
    value: enabled ? 'true' : 'false',
  });
  if (error) throw new Error(formatSupabaseError(error));
}

export async function fetchPostalCoords(postals: string[]): Promise<Map<string, [number, number]>> {
  const map = new Map<string, [number, number]>();
  if (postals.length === 0) return map;

  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('sg_postal_coords')
    .select('postal, lat, lng')
    .in('postal', postals);

  if (error) throw new Error(formatSupabaseError(error));

  for (const row of data ?? []) {
    const r = row as Record<string, unknown>;
    const postal = String(r.postal ?? '');
    const lat = Number(r.lat);
    const lng = Number(r.lng);
    if (postal && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      map.set(postal, [lat, lng]);
    }
  }

  return map;
}

export async function upsertPostalCoord(postal: string, lat: number, lng: number): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db.from('sg_postal_coords').upsert({ postal, lat, lng });
  if (error) throw new Error(formatSupabaseError(error));
}

export async function updateStopCoordinates(id: string, lat: number, lng: number): Promise<void> {
  if (!id) return;
  const db = getDbSupabase('dem');
  const { error } = await db.from('trip_order_mapping').update({ latitude: lat, longitude: lng }).eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function saveStopSequences(updates: { id: string; stop_sequence: number }[]): Promise<void> {
  const valid = updates.filter((row) => row.id);
  if (valid.length === 0) return;

  const db = getDbSupabase('dem');
  const results = await Promise.all(
    valid.map((row) =>
      db.from('trip_order_mapping').update({ stop_sequence: row.stop_sequence }).eq('id', row.id),
    ),
  );

  const failed = results.find((result) => result.error);
  if (failed?.error) throw new Error(formatSupabaseError(failed.error));
}

export async function saveTripCrew(
  tripId: string,
  driverName: string,
  assistantName: string,
): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db
    .from('delivery_trips')
    .update({
      driver_name: driverName || 'Unassigned',
      driver_assistant_name: assistantName || '',
    })
    .eq('trip_id', tripId);

  if (error) throw new Error(formatSupabaseError(error));
}

export async function moveTripOrders(
  fromTripId: string,
  orderNos: string[],
  targetTripId: string,
  tripDate: string,
  createNewTrip: boolean,
): Promise<void> {
  if (!orderNos.length || !targetTripId) return;

  const db = getDbSupabase('dem');

  if (createNewTrip) {
    const { data: existing } = await db
      .from('delivery_trips')
      .select('trip_id')
      .eq('trip_id', targetTripId)
      .maybeSingle();

    if (!existing) {
      const { error: insertError } = await db.from('delivery_trips').insert({
        trip_id: targetTripId,
        driver_name: 'Unassigned',
        status: 'Planning',
        trip_date: tripDate,
      });
      if (insertError) throw new Error(formatSupabaseError(insertError));
    }
  }

  const { error } = await db
    .from('trip_order_mapping')
    .update({ trip_id: targetTripId })
    .in('order_no', orderNos)
    .eq('trip_id', fromTripId);

  if (error) throw new Error(formatSupabaseError(error));
}

type ParsedRow = Record<string, string>;

function normalizeColName(name: string): string {
  return name.trim().toLowerCase();
}

function pickColumn(columns: string[], options: string[]): string | null {
  const normalized = new Map(columns.map((c) => [normalizeColName(c), c]));
  for (const opt of options) {
    const hit = normalized.get(opt.toLowerCase());
    if (hit) return hit;
  }
  return null;
}

function parseDateCell(raw: string, fallbackDate: string): { date: string; code: string } {
  const fallbackCode = fallbackDate.replace(/-/g, '').slice(2);
  const trimmed = raw.trim();
  if (!trimmed) return { date: fallbackDate, code: fallbackCode };

  const datePart = trimmed.split(' ')[0];
  try {
    if (datePart.includes('/')) {
      const [dd, mm, yyyy] = datePart.split('/').map(Number);
      const dt = new Date(Date.UTC(yyyy, mm - 1, dd, 12));
      const ymd = singaporeYmd(dt);
      return { date: ymd, code: ymd.replace(/-/g, '').slice(2) };
    }
    const iso = datePart.length >= 10 ? datePart.slice(0, 10) : datePart;
    const parsed = new Date(`${iso}T12:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      const ymd = singaporeYmd(parsed);
      return { date: ymd, code: ymd.replace(/-/g, '').slice(2) };
    }
  } catch {
    // use fallback
  }
  return { date: fallbackDate, code: fallbackCode };
}

async function rowsFromFile(file: File): Promise<ParsedRow[]> {
  if (file.name.toLowerCase().endsWith('.json')) {
    const text = await file.text();
    const json = JSON.parse(text) as unknown;
    if (!Array.isArray(json)) throw new Error('JSON file must contain an array of rows.');
    return json.map((row) => {
      const record: ParsedRow = {};
      if (row && typeof row === 'object') {
        for (const [k, v] of Object.entries(row as Record<string, unknown>)) {
          record[k] = v == null ? '' : String(v);
        }
      }
      return record;
    });
  }

  if (file.name.toLowerCase().endsWith('.csv')) {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
    return lines.slice(1).map((line) => {
      const cells = line.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
      const row: ParsedRow = {};
      headers.forEach((h, i) => {
        row[h] = cells[i] ?? '';
      });
      return row;
    });
  }

  const buffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const sheet = workbook.worksheets[0];
  if (!sheet) return [];

  const headers: string[] = [];
  sheet.getRow(1).eachCell((cell, col) => {
    headers[col - 1] = String(cell.value ?? '').trim();
  });

  const rows: ParsedRow[] = [];
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const record: ParsedRow = {};
    row.eachCell((cell, col) => {
      const header = headers[col - 1];
      if (!header) return;
      record[header] = cell.value == null ? '' : String(cell.value).trim();
    });
    if (Object.values(record).some((v) => v)) rows.push(record);
  });

  return rows;
}

export async function importDeliveryListFile(file: File, fallbackDate: string): Promise<number> {
  const df = await rowsFromFile(file);
  if (df.length === 0) throw new Error('No rows found in the uploaded file.');

  const columns = Object.keys(df[0]);
  const colDate = pickColumn(columns, [
    'Shipment Date',
    'Delivery Date',
    'Date',
    'Posting Date',
    'trip_date',
    'delivery_date',
  ]);
  const colNo = pickColumn(columns, ['No.', 'order_no', 'id']);
  const colCustCode = pickColumn(columns, ['Sell-to Customer No.', 'customer_code']);
  const colCustName = pickColumn(columns, ['Sell-to Customer Name', 'customer_name']);
  const colTruck = pickColumn(columns, ['Truck', 'Territory Code', 'driver_name', 'driver']);
  const colAddr1 = pickColumn(columns, ['Ship-to Address', 'address']);
  const colAddr2 = pickColumn(columns, ['Ship-to Address 2']);
  const colPost = pickColumn(columns, ['Ship-to Post Code', 'postal']);

  if (!colNo) {
    throw new Error("Missing required column for order number ('No.', 'order_no', etc.).");
  }

  type TruckGroup = {
    date: string;
    shortCode: string;
    truck: string;
    orders: {
      order_no: string;
      customer_code: string;
      customer_name: string;
      address: string;
      postal: string;
    }[];
  };

  const truckGroups = new Map<string, TruckGroup>();

  for (const row of df) {
    const orderNo = String(row[colNo] ?? '').trim();
    if (!orderNo) continue;

    const rawDate = colDate ? String(row[colDate] ?? '') : '';
    const { date: rowDate, code: rowCode } = parseDateCell(rawDate, fallbackDate);
    const postal = String(row[colPost ?? ''] ?? '').replace(/\D/g, '');
    const truckName = String(row[colTruck ?? ''] ?? '').trim().toUpperCase() || 'UNASSIGNED';
    const groupKey = `${rowDate}_${truckName}`;

    if (!truckGroups.has(groupKey)) {
      truckGroups.set(groupKey, {
        date: rowDate,
        shortCode: rowCode,
        truck: truckName,
        orders: [],
      });
    }

    const addrParts = [colAddr1 ? row[colAddr1] : '', colAddr2 ? row[colAddr2] : ''].filter(
      (p) => p && p !== 'nan',
    );
    const fullAddress = addrParts.join(' ').trim();

    truckGroups.get(groupKey)!.orders.push({
      order_no: orderNo,
      customer_code: String(row[colCustCode ?? ''] ?? '').replace(/nan/gi, ''),
      customer_name: String(row[colCustName ?? ''] ?? '').replace(/nan/gi, ''),
      address: fullAddress,
      postal,
    });
  }

  const tripsToInsert: Record<string, unknown>[] = [];
  const mappingsToInsert: Record<string, unknown>[] = [];

  for (const group of truckGroups.values()) {
    if (group.orders.length === 0) continue;
    group.orders.sort((a, b) => (a.postal || '999999').localeCompare(b.postal || '999999'));

    const tripId = `TRIP-${group.shortCode}-${group.truck}`;
    tripsToInsert.push({
      trip_id: tripId,
      driver_name: group.truck,
      status: 'Planning',
      trip_date: group.date,
    });

    group.orders.forEach((order, index) => {
      mappingsToInsert.push({
        trip_id: tripId,
        order_no: order.order_no,
        customer_code: order.customer_code,
        customer_name: order.customer_name,
        address: order.address,
        postal: order.postal,
        latitude: null,
        longitude: null,
        stop_sequence: index + 1,
      });
    });
  }

  const db = getDbSupabase('dem');

  if (tripsToInsert.length > 0) {
    const { error } = await db.from('delivery_trips').upsert(tripsToInsert);
    if (error) throw new Error(formatSupabaseError(error));
  }

  if (mappingsToInsert.length > 0) {
    const tripIds = tripsToInsert.map((t) => String(t.trip_id));
    const { error: deleteError } = await db.from('trip_order_mapping').delete().in('trip_id', tripIds);
    if (deleteError) throw new Error(formatSupabaseError(deleteError));

    for (let i = 0; i < mappingsToInsert.length; i += 500) {
      const chunk = mappingsToInsert.slice(i, i + 500);
      const { error: insertError } = await db.from('trip_order_mapping').insert(chunk);
      if (insertError) throw new Error(formatSupabaseError(insertError));
    }
  }

  return mappingsToInsert.length;
}

export function dispatchStatusTone(status: string): 'planning' | 'ongoing' | 'completed' | 'default' {
  const s = status.toLowerCase();
  if (s === 'planning') return 'planning';
  if (s === 'ongoing') return 'ongoing';
  if (s === 'completed') return 'completed';
  return 'default';
}
